import {Camera, CameraResultType, CameraSource} from "@capacitor/camera";
import {isPlatform} from "@ionic/react";
import {Capacitor} from "@capacitor/core";
import {StorageName} from "./StorageManager";
import {getManager} from "./Manager";
import {fileNameOnly, numberWithCommas, slugify} from "../common/SiteHelper";


export interface IFileUploadSettings
{
    mimeTypes?:string;
    fileCountMin?:number;
    fileLimit?:number;
    fileSizeMin?:number;
    fileSizeMax?:number;
    errorFileLimit?:number;
    itemTypeName?:string;
    itemTypeNamePlural?:string;
}

export class FormDataVO
{
    webPath:string;
    name: string;
    blobValue: Blob;
    filename?: string

    constructor(webPath:string, name: string, blobValue: Blob, fileName?: string)
    {
        this.webPath = webPath;
        this.name = name;
        this.blobValue = blobValue;
        this.filename = fileName;
    }
}

export const constructFormData = (formDataVOs:FormDataVO[]):FormData => {
    const formData:FormData = new FormData() as FormData;
    let fileIndex = -1;
    for(let formDataVO of formDataVOs){
        fileIndex ++;
        formData.append("file"+fileIndex, formDataVO.blobValue, formDataVO.filename);
    }
    return formData;
}

export class FormDataResponse
{
    formDataVOs:FormDataVO[];
    errors:string[];
    valid:boolean;
    webPath:string;

    //name: string, blobValue: Blob, filename?: string

    constructor(formDataVOs:FormDataVO[], errors:string[], valid:boolean, webPath:string)
    {
        this.formDataVOs = formDataVOs;
        this.errors = errors;
        this.valid = valid;
        this.webPath = webPath;
    }
}

class CacheFileState
{
    base64Data:string|null = null;
    localFilePath:string|null = null;
    inProgress:boolean = false;
    failed:boolean = false;
    callbacks:((localFilePath:string|null) => void)[] = [];
}

export interface ICacheFileStates
{
    [key: string]: CacheFileState;
}

export default class FileManager
{
    storageName:StorageName = "file";
    cacheStates:ICacheFileStates = {};

    getFileInputFormData(files: FileList, settings?:IFileUploadSettings, additionalFileCount?:number): FormDataResponse
    {
        const fileSizeMin = settings?.fileSizeMin ?? 0;
        const fileSizeMax = settings?.fileSizeMax ?? 0;
        const fileLimit = settings?.fileLimit ?? 0;
        const errorFileLimit = settings?.errorFileLimit ?? fileLimit;
        const itemTypeName = settings?.itemTypeName ?? "file";
        const itemTypeNamePlural = settings?.itemTypeNamePlural ?? "files";

        let fileCount = additionalFileCount ? additionalFileCount: 0;

        const errors:string[] = [];

        const formDataVOs:FormDataVO[] = [];
        let valid = false;
        let webPath = "";
        for(let i = 0; i < files.length; i ++)
        {
            const file = files[i];
            const fileSize = file.size;
            let errorMessage = null;
            if(!fileSize) {
                errorMessage = "Could not process file";
            } else if(fileSizeMax && fileSize > fileSizeMax) {
                const fileSizeMaxKB = fileSizeMax / 1024;
                errorMessage = "Max file size (" + numberWithCommas(fileSizeMaxKB)+" kb) exceeded. Selected file size is " + numberWithCommas(Math.ceil(fileSize / 1024))+" kb";
            } else if(fileSizeMin && fileSize < fileSizeMin) {
                const fileSizeMinKB = fileSizeMin / 1024;
                errorMessage = "Min file size (" + numberWithCommas(fileSizeMinKB)+" kb) needed. Selected file size is " + numberWithCommas(Math.ceil(fileSize / 1024))+" kb";
            } else if(fileLimit > 0 && fileCount >= fileLimit){
                const useItemTypeName = fileLimit !== 1 ? itemTypeNamePlural : itemTypeName;
                errorMessage = "A maximum of " + errorFileLimit + " " + useItemTypeName + " can  be uploaded";
            }
            if(errorMessage){
                errors.push(errorMessage);
            } else {
                formDataVOs.push(new FormDataVO(URL.createObjectURL(file), fileNameOnly(file.name), file, file.name))
                valid = true;
                fileCount ++;
                webPath = URL.createObjectURL(file)
            }
        }
        return new FormDataResponse(formDataVOs, errors, valid, webPath);
    }

    async getCameraCaptureFormData(): Promise<FormDataResponse>
    {

        /*

Nb. for android this needs added to /resources/assets/capacitor/android/app/src/main/AndroidManifest.xml even though some docs appear to say otherise
    <queries>
        <intent>
            <action android:name="android.media.action.IMAGE_CAPTURE" />
        </intent>
    </queries>
         */
        const formDataVOs:FormDataVO[] = [];
        let errorMessage:string = "";
        let valid = false;

        let webPath = "";
        try
        {
            const photo = await Camera.getPhoto({
                resultType: CameraResultType.Uri,
                source: CameraSource.Camera,
                quality: 100,
            });

            // "hybrid" will detect Cordova or Capacitor;
            webPath = !isPlatform('hybrid') ? photo.webPath! : Capacitor.convertFileSrc(photo.webPath!);
            const response = await fetch(webPath);
            const photoBlob: Blob =  await response.blob();
            if(photoBlob){
                const fileName = "camera-" + fileNameOnly(webPath);
                formDataVOs.push(new FormDataVO(webPath, fileName, photoBlob, fileName + '.' + photo.format));
                valid = true;
            } else {
                errorMessage = "Could not process file";
            }
        } catch (error:any)
        {
            if(error.message !== "User cancelled photos app"){
                errorMessage = error.message ? error.message : "Could not capture image";
            }
        }

        const errors = errorMessage ? [errorMessage] : [];
        return new FormDataResponse(formDataVOs, errors, valid, webPath);
    }

    async addFileToCache(pathOrURl:string, callback?:(localFilePath:string|null) => void): Promise<void>
    {
        if(this.cacheStates[pathOrURl]){
            if(callback){
                this.getCachePath(pathOrURl, callback);
            }
            return;
        }

        const cacheState = new CacheFileState();
        cacheState.inProgress = true;
        if(callback){
            cacheState.callbacks.push(callback);
        }
        this.cacheStates[pathOrURl] = cacheState;
        let base64Data:string|null = null;
        try
        {
            base64Data = await this.base64FromPath(pathOrURl)
        }
        catch (_:any){ }
        if(!base64Data){
            this.onCacheProcessComplete(cacheState);
            return;
        }
        cacheState.base64Data = base64Data;

        await getManager().storage.set(pathOrURl, base64Data, this.storageName);
        if(cacheState.callbacks.length){
            await this.cacheProcessLocalFilePath(cacheState);
        } else {
            this.onCacheProcessComplete(cacheState);
        }

    }

    getCachePath(pathOrURl:string, callback:(localFilePath:string|null) => void): void
    {
        let cacheState:CacheFileState|null = this.cacheStates[pathOrURl] ?? null;
        if(cacheState){
            if(cacheState.inProgress){
                cacheState.callbacks.push(callback);
            } else if(cacheState.failed || !cacheState.base64Data){
                callback(null);
            } else if(cacheState.localFilePath){
                callback(cacheState.localFilePath);
            } else {
                cacheState.callbacks.push(callback);
                this.cacheProcessLocalFilePath(cacheState).then(_ => {});
            }
            return;
        }

        cacheState = new CacheFileState();
        this.cacheStates[pathOrURl] = cacheState;
        cacheState.callbacks.push(callback);
        cacheState.inProgress = true;
        getManager().storage.get(pathOrURl, this.storageName).then(base64Data => {
            if(!base64Data){
                this.onCacheProcessComplete(cacheState!);
                return;
            }
            cacheState!.base64Data = base64Data;
            this.cacheProcessLocalFilePath(cacheState!).then(_ => {});
        });
    }

    async cacheProcessLocalFilePath(cacheState:CacheFileState): Promise<void>
    {
        cacheState.inProgress = true;
        let localFilePath:string|null = null;
        if(cacheState.base64Data){

            try
            {
                const response = await fetch(cacheState.base64Data);
                const photoBlob: Blob =  await response.blob();
                localFilePath = URL.createObjectURL(photoBlob);
            }
            catch (_:any){ }
        }
        cacheState.localFilePath = localFilePath ? localFilePath : null;
        this.onCacheProcessComplete(cacheState);
    }

    onCacheProcessComplete(cacheState:CacheFileState): void
    {
        cacheState.inProgress = false;
        cacheState.failed = !cacheState.localFilePath;
        for(let i = 0; i < cacheState.callbacks.length; ++i){
            cacheState.callbacks[i](cacheState.localFilePath);
        }
        cacheState.callbacks.length = 0;
    }

    async base64FromPath(pathOrURl:string): Promise<string|null>
    {
        const response = await fetch(pathOrURl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result);
                } else {
                    reject()
                }
            };
            reader.readAsDataURL(blob);
        });
    }

}